import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useFloating,
  useHover,
  useClick,
  useDismiss,
  useInteractions,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react';
import './HoverClickPopup.css';

/**
 * Reusable hover/click popup component
 * Hover to show popup, click to pin it open
 * 
 * @param {ReactNode} trigger - The element that triggers the popup
 * @param {ReactNode} children - The popup content
 * @param {string} placement - Popup placement: 'top' | 'bottom' | 'left' | 'right'
 * @param {number} offsetValue - Distance from trigger element
 * @param {boolean} showCloseButton - Show close button when pinned
 * @param {boolean} hoverEnabled - Enable hover to show popup (default: true)
 * @param {string} className - Additional CSS class for popup container
 */
export default function HoverClickPopup({ 
  trigger,
  children,
  placement = 'top',
  offsetValue = 8,
  showCloseButton = true,
  hoverEnabled = true,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  
  // Floating UI setup
  const { x, y, strategy, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetValue),
      flip(),
      shift({ padding: 8 }),
    ],
  });
  
  // Interactions
  const hover = useHover(context, {
    enabled: hoverEnabled && !isPinned,  // Disable hover if hoverEnabled is false
    delay: { open: 200, close: 100 }, // Small delay to prevent flicker
  });
  
  const click = useClick(context, {
    enabled: true,
  });
  
  const dismiss = useDismiss(context, {
    enabled: hoverEnabled && !isPinned,  // Only dismiss on outside click if hover is enabled
  });
  
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
  ]);
  
  // Handle click to pin/unpin
  const handleTriggerClick = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      // When hover is disabled, always pin on click
      setIsPinned(!hoverEnabled || true);
      setIsOpen(true);
    } else if (isPinned) {
      // If already pinned, close it
      setIsPinned(false);
      setIsOpen(false);
    } else if (hoverEnabled) {
      // If hover is enabled and popup is open but not pinned, pin it
      setIsPinned(true);
    }
  };
  
  // Handle close button
  const handleClose = (e) => {
    e.stopPropagation();
    setIsPinned(false);
    setIsOpen(false);
  };
  
  // Clone trigger element with ref and props
  const triggerElement = React.cloneElement(trigger, {
    ref: refs.setReference,
    ...getReferenceProps(),
    onClick: handleTriggerClick,
    className: `${trigger.props.className || ''} ${isPinned ? 'popup-trigger-active' : ''}`,
  });
  
  return (
    <>
      {triggerElement}
      
      {isOpen && createPortal(
        <div
          ref={refs.setFloating}
          className={`hover-click-popup ${isPinned ? 'pinned' : ''} ${className}`}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            zIndex: 9999,
          }}
          {...getFloatingProps()}
        >
          {/* Close button for pinned popups */}
          {isPinned && showCloseButton && (
            <button 
              className="hover-click-popup-close"
              onClick={handleClose}
              aria-label="Close popup"
            >
              ×
            </button>
          )}
          
          {/* Popup content */}
          <div className="hover-click-popup-content">
            {children}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}